import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Edit3,
    Eye,
    FileCode,
    FileText,
    Link2,
    List,
    Loader2,
    Plus,
    RefreshCw,
    Save,
    Trash2,
    Wrench,
    X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { CodeEditorPreview } from '../shared/CodeEditorPreview';

const locatorTypes = ['role', 'label', 'text', 'placeholder', 'testid', 'locator'];
const roleOptions = ['button', 'link', 'table', 'checkbox', 'heading', 'textbox', 'combobox', 'menuitem'];

const isAbsoluteHttpUrl = (value = '') => {
    try {
        const parsed = new URL(String(value || '').trim());
        return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.host);
    } catch (_err) {
        return false;
    }
};

const normalizeFullPageUrl = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
        return new URL(raw).href;
    } catch (_err) {
        return raw;
    }
};

const getPOMPageUrl = (pom = {}) => String(pom.pageUrl || pom.page_url || '').trim();

const withoutTransientId = (value = {}) => {
    const { id: _id, ...rest } = value || {};
    return rest;
};

const buildPOMDraftSignature = (draft = {}) => JSON.stringify({
    pageName: String(draft.pomName || ''),
    pageUrl: String(draft.pomUrl || ''),
    locators: (draft.locators || []).map(withoutTransientId),
    functions: (draft.functions || []).map(withoutTransientId),
});

const getPOMDraftValidationError = (draft = {}) => {
    const pomName = String(draft.pomName || '');
    const pomUrl = String(draft.pomUrl || '');
    const locators = Array.isArray(draft.locators) ? draft.locators : [];
    const functions = Array.isArray(draft.functions) ? draft.functions : [];

    if (!pomName.trim()) return 'POM name is required.';
    if (!pomUrl.trim()) return 'Page URL is required.';
    if (!isAbsoluteHttpUrl(pomUrl)) {
        return 'Page URL must be a full http(s) URL, for example https://your-app.example.com/login.';
    }

    for (const locator of locators) {
        if (!String(locator?.name || '').trim()) return 'All locators must have a name.';
        if (locator.type === 'role' && !locator.role) return 'Role locators must include a role.';
        if (locator.type !== 'role' && !String(locator?.value || '').trim()) {
            return `Locator '${locator.name}' must include a value.`;
        }
    }

    const locatorNames = locators.map((locator) => String(locator?.name || '').trim()).filter(Boolean);
    if (new Set(locatorNames).size !== locatorNames.length) return 'Locator names must be unique.';

    for (const method of functions) {
        if (!String(method?.name || '').trim()) return 'All methods must have a name.';
        if (!String(method?.description || '').trim()) return 'All methods must have a description.';
        if (!String(method?.body || '').trim()) {
            return `Method '${method.name}' must include a body.`;
        }
    }

    const methodNames = functions.map((method) => String(method?.name || '').trim()).filter(Boolean);
    if (new Set(methodNames).size !== methodNames.length) return 'Method names must be unique.';
    return '';
};

/**
 * A stored chain is authoritative when the backend renders the POM, so it must
 * only survive an edit that did not change the locator it represents. If the
 * reviewer switched the type, value or role, the chain now describes a
 * different element and keeping it would silently discard their edit.
 */
const chainStillMatchesLocator = (locator = {}) => {
    const chain = Array.isArray(locator.chain) ? locator.chain : null;
    if (!chain || !chain.length) return false;
    const last = chain[chain.length - 1] || {};
    if (String(last.type || '') !== String(locator.type || '')) return false;
    if (String(last.value || '') !== String(locator.value || '')) return false;
    if (locator.type === 'role' && String(last.role || '') !== String(locator.role || '')) return false;
    if (locator.type === 'text' && Boolean(last.exact) !== Boolean(locator.exact)) return false;
    return true;
};

const formatPOMLocatorForAPI = (locator = {}) => {
    const formatted = {
        name: locator.name,
        type: locator.type,
        description: locator.description || '',
    };
    [
        'tag',
        'text',
        'score',
        'strategy',
        'stabilityReason',
        'element',
        'candidates',
        'validation',
        // POM scanner fields. Editing a scanned locator in this editor must not
        // silently drop its iframe path, row template or review state.
        'frame',
        'rowTemplate',
        'nth',
        'kind',
        'group',
        'control',
        'elementKey',
        'screenshot',
        'identityVerified',
        'matchCount',
        'stability',
        'approved',
        'health',
    ].forEach((field) => {
        if (locator[field] !== undefined && locator[field] !== null && locator[field] !== '') {
            formatted[field] = locator[field];
        }
    });

    if (chainStillMatchesLocator(locator)) formatted.chain = locator.chain;

    if (locator.type === 'role') {
        formatted.role = locator.role;
        if (locator.value) formatted.value = locator.value;
        if (locator.options) formatted.options = locator.options;
    } else {
        formatted.value = locator.value;
        if (locator.type === 'text' && locator.exact) formatted.exact = locator.exact;
    }

    return formatted;
};

const buildScannerAssetUrl = (pdfId, pageName, assetName) => {
    if (!pdfId || !pageName || !assetName) return '';
    return `${API_BASE_URL}${API_ENDPOINTS.POM_SCANNER_ASSET(pdfId, pageName, assetName)}`;
};

const buildApplicationScannerAssetUrl = (applicationId, pageName, assetName) => {
    if (!applicationId || !pageName || !assetName) return '';
    return `${API_BASE_URL}${API_ENDPOINTS.POM_SCANNER_APPLICATION_ASSET(applicationId, pageName, assetName)}`;
};

/** A POM from the application's shared catalog, listed here but owned there. */
const isApplicationPom = (pom = {}) => String(pom.scope || 'local') === 'application';

/**
 * Review badges for scanner-produced POM metadata such as page state, shared
 * component flag and the last health check.
 */
const scannerReviewBadges = (pom = {}) => {
    const badges = [];
    const pageState = String(pom.pageState || '').trim();
    const health = pom.health && typeof pom.health === 'object' ? pom.health : null;

    if (pageState && pageState !== 'default') {
        badges.push({
            key: 'state',
            label: pageState,
            className: 'border-violet-200 bg-violet-50 text-violet-800',
        });
    }
    if (pom.shared) {
        badges.push({
            key: 'shared',
            label: 'Shared component',
            className: 'border-sky-200 bg-sky-50 text-sky-800',
        });
    }
    if (health?.state && health.state !== 'unknown') {
        const stale = Number(health.counts?.stale || 0);
        const drifted = Number(health.counts?.healed || 0);
        const className = health.state === 'ok'
            ? 'border-emerald-200 bg-white text-emerald-700'
            : health.state === 'stale'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-amber-200 bg-white text-amber-800';
        const detail = health.state === 'ok'
            ? 'Locators healthy'
            : `${stale} stale, ${drifted} drifted`;
        badges.push({ key: 'health', label: detail, className });
    }
    return badges;
};

const coerceCount = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeFeaturePomPages = (featurePom = {}) => {
    const source = featurePom && typeof featurePom === 'object' && !Array.isArray(featurePom)
        ? featurePom
        : {};
    const rawPages = Array.isArray(source.pages) ? source.pages : [];
    const fallbackPage = {
        pageName: source.page_name || source.pageName || source.name || 'CapturedFeaturePage',
        pageUrl: source.page_url || source.pageUrl || source.url || '',
        locators: source.locator_count || source.locators || 0,
        functions: source.function_count || source.functions || 0,
        generatedOn: source.generated_at || source.generatedOn || null,
        typescript: source.typescript || source.code || '',
    };
    const pages = rawPages.length || source.page_count || source.typescript ? rawPages.length ? rawPages : [fallbackPage] : [];

    return pages.map((page, index) => {
        const pageObject = page && typeof page === 'object' ? page : {};
        const pageName = String(
            pageObject.pageName
            || pageObject.page_name
            || pageObject.name
            || fallbackPage.pageName
            || `CapturedPage${index + 1}`
        ).trim() || `CapturedPage${index + 1}`;
        const pageUrl = String(
            pageObject.pageUrl
            || pageObject.page_url
            || pageObject.url
            || fallbackPage.pageUrl
            || ''
        ).trim();
        const locatorItems = Array.isArray(pageObject.locator_items)
            ? pageObject.locator_items
            : Array.isArray(pageObject.locators_detail)
                ? pageObject.locators_detail
                : [];
        const functionItems = Array.isArray(pageObject.function_items)
            ? pageObject.function_items
            : Array.isArray(pageObject.functions_detail)
                ? pageObject.functions_detail
                : [];

        return {
            ...pageObject,
            pageName,
            pageUrl,
            locators: coerceCount(pageObject.locator_count ?? pageObject.locators ?? locatorItems.length),
            functions: coerceCount(pageObject.function_count ?? pageObject.functions ?? functionItems.length),
            locatorItems,
            functionItems,
            generatedOn: pageObject.generated_at || pageObject.generatedOn || source.generated_at || null,
            source: 'feature_capture',
            typescript: pageObject.typescript || pageObject.code || source.typescript || '',
        };
    });
};

export const GeneratePOMStep = ({
    pdfId,
    applicationId = '',
    pomCatalogScope = 'source',
    initialPOMName = '',
    initialViewMode = '',
    bulkJob = null,
    bulkItems = [],
    onBack,
    onSuccess,
    onNext,
    onInternalBackChange,
    continueToAutomationLoading = false,
    continueDisabled = false,
    continueDisabledReason = '',
    backLabel = 'Back to Test Cases',
    showBackButton = true,
    automationMode = 'pdf',
    featurePom = null,
    onFeatureGeneratePom = null,
    featurePomLoading = false,
    onPOMChange = null,
    onManageApplicationPoms = null,
    onOpenApplicationPom = null,
}) => {
    const isFeatureMode = automationMode === 'feature';
    const isApplicationCatalog = String(pomCatalogScope || '').trim().toLowerCase() === 'application';
    const scopedApplicationId = String(applicationId || '').trim();
    const hasPomCatalogScope = isApplicationCatalog ? Boolean(scopedApplicationId) : Boolean(pdfId);
    const hasApplicationPomManager = isApplicationCatalog && typeof onManageApplicationPoms === 'function';
    const hasApplicationPomRoute = isApplicationCatalog && typeof onOpenApplicationPom === 'function';
    const [viewMode, setViewMode] = useState('list');
    const [editorTab, setEditorTab] = useState('structured');
    const [selectedPOMName, setSelectedPOMName] = useState(null);

    const [pomsList, setPomsList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState(null);

    const [pomName, setPomName] = useState('');
    const [pomUrl, setPomUrl] = useState('');
    const [locators, setLocators] = useState([]);
    const [functions, setFunctions] = useState([]);
    const [originalPomName, setOriginalPomName] = useState(null);
    const [originalLocators, setOriginalLocators] = useState([]);
    const [originalFunctions, setOriginalFunctions] = useState([]);
    const [selectedPomMeta, setSelectedPomMeta] = useState(null);
    const [pomFiles, setPomFiles] = useState([]);
    const [pomFileDrafts, setPomFileDrafts] = useState({});
    const [selectedPomFileName, setSelectedPomFileName] = useState('');
    const [loadingPomFiles, setLoadingPomFiles] = useState(false);
    const [savingPomFiles, setSavingPomFiles] = useState(false);
    const [pomFileError, setPomFileError] = useState(null);
    const [pomFileSuccess, setPomFileSuccess] = useState(null);
    const [pomFileWarnings, setPomFileWarnings] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedLocator, setExpandedLocator] = useState(null);
    const [expandedFunction, setExpandedFunction] = useState(null);
    const [pendingDeletePom, setPendingDeletePom] = useState(null);
    const [showFullPageUrlModal, setShowFullPageUrlModal] = useState(false);
    const [publishingPomNames, setPublishingPomNames] = useState(new Set());
    const [autosaveStatus, setAutosaveStatus] = useState('idle');
    const autosaveTimerRef = useRef(null);
    const saveQueueRef = useRef(Promise.resolve());
    const lastSavedSignatureRef = useRef('');
    const latestDraftRef = useRef(null);
    const flushPOMEditRef = useRef(() => Promise.resolve(null));
    const resetEditorState = () => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        setPomName('');
        setPomUrl('');
        setLocators([]);
        setFunctions([]);
        setSelectedPOMName(null);
        setEditorTab('structured');
        setOriginalPomName(null);
        setOriginalLocators([]);
        setOriginalFunctions([]);
        setSelectedPomMeta(null);
        setPomFiles([]);
        setPomFileDrafts({});
        setSelectedPomFileName('');
        setPomFileError(null);
        setPomFileSuccess(null);
        setPomFileWarnings([]);
        setExpandedLocator(null);
        setExpandedFunction(null);
        setError(null);
        setSuccess(null);
        setAutosaveStatus('idle');
        lastSavedSignatureRef.current = '';
    };

    useEffect(() => {
        if (!onInternalBackChange) return undefined;

        if (viewMode === 'list') {
            onInternalBackChange(false, null);
            return undefined;
        }

        onInternalBackChange(true, async () => {
            await flushPOMEditRef.current({ keepalive: false, silent: true });
            resetEditorState();
            setViewMode('list');
        });

        return () => onInternalBackChange(false, null);
    }, [viewMode, onInternalBackChange]);

    const fetchPOMsList = useCallback(async () => {
        if (!hasPomCatalogScope) {
            const fallbackPages = isFeatureMode ? normalizeFeaturePomPages(featurePom) : [];
            setListError(fallbackPages.length > 0
                ? null
                : isFeatureMode
                    ? 'This captured flow is missing its artifact source ID. Reload the flow and try again.'
                    : isApplicationCatalog
                        ? 'Missing application context. Reopen Page Objects and try again.'
                        : 'Missing pdf_id. Please upload/select a PDF first.');
            setPomsList(fallbackPages);
            setLoadingList(false);
            return;
        }

        setLoadingList(true);
        setListError(null);

        try {
            const url = isApplicationCatalog
                ? API_ENDPOINTS.POM_SCANNER_APPLICATION_POMS(scopedApplicationId, {
                    adopt: false,
                    fast: true,
                    includeFeatureCapture: false,
                })
                : `${API_ENDPOINTS.POMS}?pdf_id=${encodeURIComponent(pdfId)}`;
            const response = await apiFetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Failed to fetch POMs');

            const result = await response.json();
            const rawPages = isApplicationCatalog ? result.poms : result.pages;
            const savedPages = (Array.isArray(rawPages) ? rawPages : []).map((pom) => (
                isApplicationCatalog
                    ? {
                        ...pom,
                        scope: 'application',
                        locators: pom.locators ?? pom.locatorCount ?? 0,
                        functions: pom.functions ?? pom.functionCount ?? 0,
                    }
                    : pom
            ));
            const fallbackPages = isFeatureMode ? normalizeFeaturePomPages(featurePom) : [];
            setPomsList(savedPages.length > 0 || !isFeatureMode ? savedPages : fallbackPages);
        } catch (err) {
            const fallbackPages = isFeatureMode ? normalizeFeaturePomPages(featurePom) : [];
            setPomsList(fallbackPages);
            setListError(fallbackPages.length > 0 ? null : (err.message || 'Unable to load POM list.'));
        } finally {
            setLoadingList(false);
        }
    }, [featurePom, hasPomCatalogScope, isApplicationCatalog, isFeatureMode, pdfId, scopedApplicationId]);

    const fetchPOMDetails = useCallback(async (name) => {
        if (!hasPomCatalogScope) {
            setError(isApplicationCatalog
                ? 'Missing application context. Reopen Page Objects and try again.'
                : 'Missing pdf_id. Please upload/select a PDF first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const url = isApplicationCatalog
                ? `${API_ENDPOINTS.POM_SCANNER_POM(name)}?application_id=${encodeURIComponent(scopedApplicationId)}`
                : `${API_ENDPOINTS.POMS}/${name}?pdf_id=${encodeURIComponent(pdfId)}`;
            const response = await apiFetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('POM not found');

            const result = await response.json();

            setPomName(result.pageName || '');
            setPomUrl(String(result.pageUrl || '').trim());
            setSelectedPOMName(name);
            setOriginalPomName(name);
            setSelectedPomMeta({
                source: result.source || '',
                generated_from: result.generated_from || '',
                typescript_edit_mode: result.typescript_edit_mode || '',
                custom_typescript: Boolean(result.custom_typescript),
                custom_typescript_updated_at: result.custom_typescript_updated_at || '',
                scope: result.scope || (isApplicationCatalog ? 'application' : 'local'),
                pageState: result.pageState || '',
                approved: Boolean(result.approved),
                pomVersion: result.pomVersion || 0,
                shared: Boolean(result.shared),
                screenshot: result.screenshot || '',
            });
            setPomFiles([]);
            setPomFileDrafts({});
            setSelectedPomFileName('');
            setPomFileError(null);
            setPomFileSuccess(null);
            setPomFileWarnings([]);

            const mappedLocators = (result.locators || []).map((loc, idx) => ({
                ...loc,
                id: Date.now() + idx,
                exact: Boolean(loc.exact),
            }));

            const mappedFunctions = (result.functions || []).map((func, idx) => ({
                ...func,
                id: Date.now() + idx + 10000,
                returnType: func.returnType || 'Promise<void>',
            }));

            lastSavedSignatureRef.current = buildPOMDraftSignature({
                pomName: result.pageName || '',
                pomUrl: String(result.pageUrl || '').trim(),
                locators: mappedLocators,
                functions: mappedFunctions,
            });
            setAutosaveStatus('saved');
            setLocators(mappedLocators);
            setFunctions(mappedFunctions);
            setOriginalLocators(mappedLocators.map((loc) => ({ ...loc })));
            setOriginalFunctions(mappedFunctions.map((func) => ({ ...func })));
        } catch (err) {
            setError(err.message || 'Unable to load POM details.');
        } finally {
            setLoading(false);
        }
    }, [hasPomCatalogScope, isApplicationCatalog, pdfId, scopedApplicationId]);

    const fetchPOMFiles = useCallback(async (name, options = {}) => {
        const targetName = String(name || selectedPOMName || pomName || '').trim();
        if (!hasPomCatalogScope || !targetName) return null;

        setLoadingPomFiles(true);
        setPomFileError(null);
        if (!options.keepMessage) {
            setPomFileSuccess(null);
            setPomFileWarnings([]);
        }

        try {
            const url = isApplicationCatalog
                ? `${API_ENDPOINTS.POM_SCANNER_FILES(targetName)}?application_id=${encodeURIComponent(scopedApplicationId)}`
                : `${API_ENDPOINTS.POM_FILES(targetName)}?pdf_id=${encodeURIComponent(pdfId)}`;
            const response = await apiFetch(
                url,
                {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                }
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Unable to load POM files.');
            }
            const result = await response.json();
            const files = Array.isArray(result.files) ? result.files : [];
            const drafts = files.reduce((acc, file) => {
                acc[file.name] = String(file.content || '');
                return acc;
            }, {});
            setPomFiles(files);
            setPomFileDrafts(drafts);
            setSelectedPomFileName((current) => (
                current && drafts[current] !== undefined
                    ? current
                    : files[0]?.name || ''
            ));
            setSelectedPomMeta((prev) => ({
                ...(prev || {}),
                typescript_edit_mode: result.typescript_edit_mode || '',
                custom_typescript: Boolean(result.custom_typescript),
                custom_typescript_updated_at: result.custom_typescript_updated_at || '',
            }));
            return result;
        } catch (err) {
            setPomFileError(err.message || 'Unable to load POM files.');
            return null;
        } finally {
            setLoadingPomFiles(false);
        }
    }, [hasPomCatalogScope, isApplicationCatalog, pdfId, pomName, scopedApplicationId, selectedPOMName]);

    const handleEditorTabChange = async (nextTab) => {
        if (nextTab === editorTab) return;
        if (nextTab === 'files') {
            await persistPOMEdit({ keepalive: false, silent: true }).catch(() => null);
            setEditorTab('files');
            fetchPOMFiles(selectedPOMName || pomName);
            return;
        }
        setEditorTab('structured');
    };

    const updatePomFileDraft = (fileName, value) => {
        setPomFileDrafts((prev) => ({ ...prev, [fileName]: value }));
        setPomFileSuccess(null);
        setPomFileWarnings([]);
    };

    const savePOMFiles = async () => {
        const targetName = String(selectedPOMName || pomName || '').trim();
        if (!hasPomCatalogScope || !targetName) {
            setPomFileError(isApplicationCatalog
                ? 'Missing application context. Reopen the POM and try again.'
                : 'Missing POM context. Reopen the POM and try again.');
            return null;
        }
        const tsFileName = `${targetName}.ts`;
        const configFileName = `${targetName}_config.json`;
        const typescriptCode = pomFileDrafts[tsFileName];
        const configText = pomFileDrafts[configFileName];
        if (typeof typescriptCode !== 'string' || typeof configText !== 'string') {
            setPomFileError('Both TypeScript and config JSON files are required.');
            return null;
        }

        setSavingPomFiles(true);
        setPomFileError(null);
        setPomFileSuccess(null);
        setPomFileWarnings([]);
        try {
            const response = await apiFetch(
                isApplicationCatalog
                    ? API_ENDPOINTS.POM_SCANNER_FILES(targetName)
                    : API_ENDPOINTS.POM_FILES(targetName),
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...(isApplicationCatalog
                            ? { application_id: scopedApplicationId }
                            : { pdf_id: pdfId }),
                        typescriptCode,
                        configText,
                    }),
                }
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Unable to save POM files.');
            }
            const result = await response.json();
            const files = Array.isArray(result.files) ? result.files : [];
            const drafts = files.reduce((acc, file) => {
                acc[file.name] = String(file.content || '');
                return acc;
            }, {});
            setPomFiles(files);
            setPomFileDrafts(drafts);
            setSelectedPomFileName((current) => (
                current && drafts[current] !== undefined
                    ? current
                    : files[0]?.name || ''
            ));
            setSelectedPomMeta((prev) => ({
                ...(prev || {}),
                typescript_edit_mode: result.typescript_edit_mode || 'custom',
                custom_typescript: Boolean(result.custom_typescript),
                custom_typescript_updated_at: result.custom_typescript_updated_at || '',
            }));
            setPomFileWarnings(Array.isArray(result.validation_warnings) ? result.validation_warnings : []);
            setPomFileSuccess('POM files saved.');
            if (onPOMChange) await onPOMChange(result);
            await fetchPOMsList();
            return result;
        } catch (err) {
            setPomFileError(err.message || 'Unable to save POM files.');
            return null;
        } finally {
            setSavingPomFiles(false);
        }
    };

    useEffect(() => {
        if (viewMode === 'list') {
            fetchPOMsList();
        }
    }, [viewMode, fetchPOMsList]);

    useEffect(() => {
        const name = String(initialPOMName || '').trim();
        if (!name) return;
        setEditorTab('structured');
        setViewMode(initialViewMode === 'edit' ? 'edit' : 'view');
        fetchPOMDetails(name);
    }, [fetchPOMDetails, initialPOMName, initialViewMode]);

    useEffect(() => {
        if (!pendingDeletePom) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape' && !loading) {
                setPendingDeletePom(null);
            }
        };

        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [pendingDeletePom, loading]);

    useEffect(() => {
        if (!showFullPageUrlModal) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setShowFullPageUrlModal(false);
            }
        };

        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [showFullPageUrlModal]);

    const handleViewPOM = (name) => {
        setEditorTab('structured');
        setViewMode('view');
        fetchPOMDetails(name);
    };

    const handleEditPOM = (name) => {
        setEditorTab('structured');
        setViewMode('edit');
        fetchPOMDetails(name);
    };

    const openPOM = (pomOrName, mode = 'view') => {
        const pageName = String(
            typeof pomOrName === 'string'
                ? pomOrName
                : pomOrName?.pageName || pomOrName?.page_name || ''
        ).trim();
        if (!pageName) return;
        if (hasApplicationPomRoute) {
            onOpenApplicationPom(
                typeof pomOrName === 'string' ? { pageName } : pomOrName,
                mode === 'edit' ? 'edit' : 'view'
            );
            return;
        }
        if (mode === 'edit') {
            handleEditPOM(pageName);
            return;
        }
        handleViewPOM(pageName);
    };

    const handleCreatePOM = async () => {
        if (isApplicationCatalog) {
            if (hasApplicationPomManager) {
                onManageApplicationPoms();
                return;
            }
            setError('Use the application Page Objects page to create shared application POMs.');
            return;
        }
        if (isFeatureMode) {
            if (!onFeatureGeneratePom) return;
            setError(null);
            setSuccess(null);
            try {
                await onFeatureGeneratePom();
                await fetchPOMsList();
                setSuccess('Page objects generated from the captured flow.');
            } catch (err) {
                setError(err?.message || 'Unable to generate page objects from the captured flow.');
            }
            return;
        }
        resetEditorState();
        setEditorTab('structured');
        setViewMode('create');
    };

    const handleManageApplicationPoms = () => {
        if (hasApplicationPomManager) {
            onManageApplicationPoms();
            return;
        }
        setError('Open the application Page Objects page to manage shared POMs.');
    };

    const addLocator = () => {
        const newLocator = {
            id: Date.now(),
            name: '',
            type: 'role',
            role: 'button',
            value: '',
            options: '',
            description: '',
            exact: false,
        };
        setLocators((prev) => [...prev, newLocator]);
        setExpandedLocator(newLocator.id);
    };

    const updateLocator = (id, field, value) => {
        setLocators((prev) => prev.map((loc) => (loc.id === id ? { ...loc, [field]: value } : loc)));
    };

    const deleteLocator = (id) => {
        setLocators((prev) => prev.filter((loc) => loc.id !== id));
    };

    const addFunction = () => {
        const newFunction = {
            id: Date.now(),
            name: '',
            description: '',
            parameters: '',
            body: '',
            returnType: 'Promise<void>',
        };
        setFunctions((prev) => [...prev, newFunction]);
        setExpandedFunction(newFunction.id);
    };

    const updateFunction = (id, field, value) => {
        setFunctions((prev) => prev.map((func) => (func.id === id ? { ...func, [field]: value } : func)));
    };

    const deleteFunction = (id) => {
        setFunctions((prev) => prev.filter((func) => func.id !== id));
    };

    const validateForm = () => {
        const validationError = getPOMDraftValidationError({
            pomName,
            pomUrl,
            locators,
            functions,
        });
        if (!validationError) return true;
        setError(validationError);
        return false;
    };

    latestDraftRef.current = {
        viewMode,
        editorTab,
        pdfId,
        applicationId: scopedApplicationId,
        pomName,
        pomUrl,
        locators,
        functions,
        originalPomName,
        originalLocators,
        originalFunctions,
    };

    const persistPOMEdit = useCallback((options = {}) => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }

        const runSave = async () => {
            const snapshot = latestDraftRef.current;
            if (
                snapshot?.viewMode !== 'edit'
                || snapshot?.editorTab === 'files'
                || (isApplicationCatalog ? !snapshot?.applicationId : !snapshot?.pdfId)
                || !snapshot?.originalPomName
            ) {
                return null;
            }

            const validationError = getPOMDraftValidationError(snapshot);
            if (validationError) {
                setAutosaveStatus('waiting');
                if (!options.silent) setError(validationError);
                return null;
            }

            const signature = buildPOMDraftSignature(snapshot);
            if (signature === lastSavedSignatureRef.current && !options.force) {
                setAutosaveStatus('saved');
                return { alreadySaved: true, pageName: snapshot.originalPomName };
            }

            setAutosaveStatus('saving');
            const formattedLocators = snapshot.locators.map(formatPOMLocatorForAPI);
            const formattedFunctions = snapshot.functions.map((method) => ({
                name: method.name,
                description: method.description,
                parameters: method.parameters || '',
                body: method.body,
                returnType: method.returnType,
            }));
            const deletedLocators = snapshot.originalLocators
                .filter((original) => !snapshot.locators.some((current) => current.name === original.name))
                .map((locator) => locator.name);
            const deletedFunctions = snapshot.originalFunctions
                .filter((original) => !snapshot.functions.some((current) => current.name === original.name))
                .map((method) => method.name);

            try {
                const updateUrl = isApplicationCatalog
                    ? API_ENDPOINTS.POM_SCANNER_POM(snapshot.originalPomName)
                    : `${API_ENDPOINTS.POM_UPDATE}/${encodeURIComponent(snapshot.originalPomName)}`;
                const response = await apiFetch(
                    updateUrl,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            ...(isApplicationCatalog
                                ? { application_id: snapshot.applicationId }
                                : { pdf_id: snapshot.pdfId }),
                            pageName: snapshot.pomName,
                            pageUrl: normalizeFullPageUrl(snapshot.pomUrl),
                            locatorsToAdd: formattedLocators,
                            locatorsToDelete: deletedLocators,
                            functionsToAdd: formattedFunctions,
                            functionsToDelete: deletedFunctions,
                        }),
                        keepalive: Boolean(options.keepalive),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to update POM');
                }

                const result = await response.json();
                const nextOriginalName = result.pageName || snapshot.pomName;
                lastSavedSignatureRef.current = signature;
                latestDraftRef.current = {
                    ...latestDraftRef.current,
                    originalPomName: nextOriginalName,
                    originalLocators: snapshot.locators.map((locator) => ({ ...locator })),
                    originalFunctions: snapshot.functions.map((method) => ({ ...method })),
                };
                setOriginalPomName(nextOriginalName);
                setSelectedPOMName(nextOriginalName);
                setOriginalLocators(snapshot.locators.map((locator) => ({ ...locator })));
                setOriginalFunctions(snapshot.functions.map((method) => ({ ...method })));
                setAutosaveStatus(
                    buildPOMDraftSignature(latestDraftRef.current) === signature ? 'saved' : 'pending'
                );
                if (onPOMChange) {
                    await onPOMChange(result);
                }
                return result;
            } catch (saveError) {
                setAutosaveStatus('error');
                if (!options.silent) setError(saveError.message || 'Failed to update POM.');
                throw saveError;
            }
        };

        const queuedSave = saveQueueRef.current.catch(() => null).then(runSave);
        saveQueueRef.current = queuedSave;
        return queuedSave;
    }, [isApplicationCatalog, onPOMChange]);
    flushPOMEditRef.current = persistPOMEdit;

    useEffect(() => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        if (
            viewMode !== 'edit'
            || editorTab === 'files'
            || !hasPomCatalogScope
            || !originalPomName
        ) return undefined;

        const snapshot = latestDraftRef.current;
        const signature = buildPOMDraftSignature(snapshot);
        if (signature === lastSavedSignatureRef.current) {
            setAutosaveStatus('saved');
            return undefined;
        }
        if (getPOMDraftValidationError(snapshot)) {
            setAutosaveStatus('waiting');
            return undefined;
        }

        setAutosaveStatus('pending');
        autosaveTimerRef.current = window.setTimeout(() => {
            persistPOMEdit({ silent: true }).catch(() => {});
        }, 800);

        return () => {
            if (autosaveTimerRef.current) {
                window.clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
        };
    }, [
        functions,
        editorTab,
        locators,
        originalPomName,
        hasPomCatalogScope,
        pdfId,
        persistPOMEdit,
        pomName,
        pomUrl,
        viewMode,
    ]);

    useEffect(() => {
        const flushPendingEdit = (keepalive) => {
            persistPOMEdit({ keepalive, silent: true }).catch(() => {});
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flushPendingEdit(true);
        };
        const handlePageHide = () => flushPendingEdit(true);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            flushPendingEdit(false);
        };
    }, [persistPOMEdit]);

    const handleSavePOM = async () => {
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;

        setLoading(true);

        try {
            if (viewMode === 'edit') {
                const result = await persistPOMEdit({ force: false, silent: false });
                setSuccess(`POM '${pomName}' updated successfully.`);
                window.setTimeout(() => {
                    if (isApplicationCatalog && onBack) {
                        onBack();
                        return;
                    }
                    resetEditorState();
                    setViewMode('list');
                    if (onSuccess) onSuccess(result);
                }, 500);
                return;
            }

            const resolvedPageUrl = normalizeFullPageUrl(pomUrl);
            const formattedLocators = locators.map(formatPOMLocatorForAPI);
            const formattedFunctions = functions.map((func) => ({
                name: func.name,
                description: func.description,
                parameters: func.parameters || '',
                body: func.body,
                returnType: func.returnType,
            }));

            const payload = {
                pdf_id: pdfId,
                pageName: pomName,
                pageUrl: resolvedPageUrl,
                locators: formattedLocators,
                functions: formattedFunctions,
            };

            const response = await apiFetch(API_ENDPOINTS.POM_CREATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create POM');
            }

            const result = await response.json();
            if (onPOMChange) {
                await onPOMChange(result);
            }
            setSuccess(`POM '${pomName}' created successfully.`);

            window.setTimeout(() => {
                resetEditorState();
                setViewMode('list');
                if (onSuccess) onSuccess(result);
            }, 1000);
        } catch (err) {
            setError(err.message || `Failed to ${viewMode === 'create' ? 'create' : 'update'} POM.`);
        } finally {
            setLoading(false);
        }
    };

    const confirmDeletePOM = async () => {
        if (!pendingDeletePom) return;
        if (!hasPomCatalogScope) {
            setListError(isApplicationCatalog
                ? 'Missing application context. Reopen Page Objects and try again.'
                : 'Missing pdf_id. Please upload/select a PDF first.');
            return;
        }

        const name = pendingDeletePom;
        setLoading(true);
        setError(null);
        setListError(null);

        try {
            const url = isApplicationCatalog
                ? `${API_ENDPOINTS.POM_SCANNER_POM(name)}?application_id=${encodeURIComponent(scopedApplicationId)}`
                : `${API_ENDPOINTS.POMS}/${name}?pdf_id=${encodeURIComponent(pdfId)}`;
            const response = await apiFetch(url, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Failed to delete POM.');
            const result = await response.json().catch(() => ({}));
            if (onPOMChange) {
                await onPOMChange(result);
            }

            setPendingDeletePom(null);
            setSuccess(`POM '${name}' deleted successfully.`);
            fetchPOMsList();
        } catch (err) {
            setListError(err.message || 'Failed to delete POM.');
        } finally {
            setLoading(false);
        }
    };

    const publishPOMGlobally = async (name) => {
        if (!pdfId) {
            setListError('Missing pdf_id. Please upload/select a PDF first.');
            return;
        }

        setListError(null);
        setSuccess(null);
        setPublishingPomNames((prev) => new Set([...prev, name]));
        try {
            const response = await apiFetch(API_ENDPOINTS.POM_PUBLISH, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    page_name: name,
                    global_name: name,
                    overwrite: false,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to publish POM globally.');
            }
            const result = await response.json();
            setSuccess(result.message || `POM '${name}' published globally.`);
        } catch (err) {
            setListError(err.message || 'Failed to publish POM globally.');
        } finally {
            setPublishingPomNames((prev) => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    };

    const listStats = useMemo(() => {
        const totalPoms = pomsList.length;
        const totalLocators = pomsList.reduce((sum, pom) => sum + Number(pom.locators || 0), 0);
        const totalMethods = pomsList.reduce((sum, pom) => sum + Number(pom.functions || 0), 0);
        return { totalPoms, totalLocators, totalMethods };
    }, [pomsList]);
    const pomsMissingFullPageUrl = useMemo(
        () => (isFeatureMode ? [] : pomsList.filter((pom) => !isAbsoluteHttpUrl(getPOMPageUrl(pom)))),
        [isFeatureMode, pomsList]
    );
    const fullPageUrlWarning = useMemo(() => {
        if (isFeatureMode) return '';
        if (pomsMissingFullPageUrl.length === 0) return '';

        const names = pomsMissingFullPageUrl
            .map((pom) => String(pom.pageName || pom.page_name || 'Unnamed POM').trim() || 'Unnamed POM');
        const visibleNames = names.slice(0, 3).join(', ');
        const remainingCount = names.length - 3;
        const suffix = remainingCount > 0
            ? `${visibleNames}, and ${remainingCount} more`
            : visibleNames;

        return `${pomsMissingFullPageUrl.length} POM${pomsMissingFullPageUrl.length === 1 ? '' : 's'} need a full website URL before Automation: ${suffix}.`;
    }, [isFeatureMode, pomsMissingFullPageUrl]);

    const isReadOnly = viewMode === 'view';
    const isCreateView = viewMode === 'create';

    const modeTitle = isReadOnly
        ? 'View Page Object Model'
        : isCreateView
            ? 'Create Page Object Model'
            : 'Edit Page Object Model';

    const modeDescription = isReadOnly
        ? 'Review saved locators and methods in read-only mode.'
        : isCreateView
            ? 'Define page details, locators, and methods for automation generation.'
            : 'Update locators and methods to keep automation scripts stable.';

    const draftSummary = `${locators.length} locator${locators.length !== 1 ? 's' : ''} and ${functions.length} method${functions.length !== 1 ? 's' : ''}`;
    const autosaveMessage = {
        pending: 'Changes pending',
        saving: 'Saving...',
        saved: isApplicationCatalog ? 'Saved to application' : 'Saved',
        waiting: 'Complete required fields to save',
        error: 'Save failed',
    }[autosaveStatus] || '';
    const hasSavedPOM = pomsList.length > 0 || Boolean(selectedPOMName);
    const selectedPomIsScanner = selectedPomMeta?.source === 'pom_scanner';
    const selectedPomImageUrl = selectedPomIsScanner && selectedPomMeta?.screenshot
            ? (isApplicationCatalog
                ? buildApplicationScannerAssetUrl(scopedApplicationId, selectedPOMName || pomName, selectedPomMeta.screenshot)
                : buildScannerAssetUrl(pdfId, selectedPOMName || pomName, selectedPomMeta.screenshot))
            : '';

    const closeDeleteModal = () => {
        if (!loading) setPendingDeletePom(null);
    };

    const closeFullPageUrlModal = () => setShowFullPageUrlModal(false);

    const handleEditFirstMissingFullPageUrl = () => {
        const firstMissingPom = pomsMissingFullPageUrl[0] || {};
        const firstMissingPomName = String(firstMissingPom.pageName || firstMissingPom.page_name || '').trim();
        if (!firstMissingPomName) return;

        setShowFullPageUrlModal(false);
        openPOM(firstMissingPom, 'edit');
    };

    const handleContinueToSaveTests = () => {
        if (!hasSavedPOM) {
            setError(isFeatureMode
                ? 'Generate page objects from the captured flow before continuing to scripts.'
                : 'Create and save at least one POM before continuing to Automation.'
            );
            return;
        }

        if (!isFeatureMode && pomsMissingFullPageUrl.length > 0) {
            setError(null);
            setShowFullPageUrlModal(true);
            return;
        }

        setError(null);
        setListError(null);
        if (onNext) onNext();
    };

    const renderPOMFileView = () => {
        const selectedFile = pomFiles.find((file) => file.name === selectedPomFileName) || pomFiles[0] || null;
        const selectedDraft = selectedFile ? pomFileDrafts[selectedFile.name] ?? '' : '';
        const isConfigFile = String(selectedFile?.language || '').toLowerCase() === 'json';
        const customTimestamp = selectedPomMeta?.custom_typescript_updated_at
            ? new Date(selectedPomMeta.custom_typescript_updated_at).toLocaleString()
            : '';

        return (
            <section className="soft-card space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700">
                            <FileCode className="h-5 w-5" />
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-slate-900">POM File View</h4>
                            <p className="mt-1 text-sm text-slate-600">
                                TypeScript is used for execution; config JSON powers catalog, selection, and generated-script context.
                            </p>
                            {selectedPomMeta?.custom_typescript && (
                                <p className="mt-1 text-xs font-semibold text-amber-700">
                                    Custom TypeScript{customTimestamp ? ` saved ${customTimestamp}` : ''}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchPOMFiles(selectedPOMName || pomName, { keepMessage: true })}
                        disabled={loadingPomFiles || savingPomFiles}
                        className="secondary-btn px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loadingPomFiles ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </button>
                </div>

                {pomFileError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span>{pomFileError}</span>
                    </div>
                )}

                {pomFileSuccess && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5" />
                        <span>{pomFileSuccess}</span>
                    </div>
                )}

                {pomFileWarnings.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        {pomFileWarnings.join(' ')}
                    </div>
                )}

                {loadingPomFiles && pomFiles.length === 0 ? (
                    <div className="soft-card-muted flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                            {pomFiles.length === 0 ? (
                                <p className="px-2 py-6 text-center text-sm text-slate-500">No POM files loaded.</p>
                            ) : pomFiles.map((file) => (
                                <button
                                    key={file.name}
                                    type="button"
                                    onClick={() => setSelectedPomFileName(file.name)}
                                    className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition last:mb-0 ${
                                        selectedPomFileName === file.name
                                            ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200'
                                            : 'text-slate-700 hover:bg-white'
                                    }`}
                                >
                                    <FileText className="h-4 w-4 shrink-0" />
                                    <span className="min-w-0 truncate">{file.name}</span>
                                </button>
                            ))}
                        </div>

                        <div className="min-w-0 space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">{selectedFile?.name || 'Select a file'}</p>
                                    {selectedFile?.sourceOfTruth && (
                                        <p className="text-xs text-slate-500">
                                            {selectedFile.sourceOfTruth === 'catalog' ? 'Catalog source' : 'Execution source'}
                                        </p>
                                    )}
                                </div>
                                {isConfigFile && (
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                        JSON
                                    </span>
                                )}
                            </div>

                            {selectedFile ? (
                                <CodeEditorPreview
                                    value={selectedDraft}
                                    language={selectedFile.language || 'typescript'}
                                    editable={!isReadOnly}
                                    onChange={(value) => updatePomFileDraft(selectedFile.name, value)}
                                    minHeight={420}
                                    maxHeight={680}
                                    placeholder={isConfigFile ? '{\n  "pageName": "LoginPage"\n}' : `export class ${pomName || 'PageObject'} {}`}
                                />
                            ) : (
                                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-10 text-center text-sm text-slate-600">
                                    Select a file to view its contents.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>
        );
    };

    const renderFullPageUrlModal = () => {
        if (!showFullPageUrlModal || pomsMissingFullPageUrl.length === 0) return null;

        return ReactDOM.createPortal(
            <div className="modal-overlay" role="presentation" onClick={closeFullPageUrlModal}>
                <div
                    className="modal-shell max-w-lg"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="full-page-url-title"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="border-b border-amber-200 bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 text-white">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h4 id="full-page-url-title" className="text-lg font-semibold">Website URL Required</h4>
                                <p className="mt-1 text-sm text-amber-50">
                                    Add full page URLs before continuing to Automation.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeFullPageUrlModal}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white hover:bg-white/20"
                                aria-label="Close website URL warning"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4 bg-white px-5 py-4">
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>{fullPageUrlWarning}</span>
                            </div>
                        </div>

                        <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200">
                            {pomsMissingFullPageUrl.map((pom, index) => {
                                const name = String(pom.pageName || pom.page_name || 'Unnamed POM').trim() || 'Unnamed POM';
                                return (
                                    <div
                                        key={`${name}-${index}`}
                                        className="flex items-start gap-3 border-b border-slate-100 px-3 py-3 last:border-b-0"
                                    >
                                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
                                            <p className="mt-0.5 text-xs font-medium text-amber-700">Full page URL required</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            onClick={closeFullPageUrlModal}
                            className="secondary-btn px-4 py-2"
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            onClick={handleEditFirstMissingFullPageUrl}
                            className="primary-btn bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2"
                        >
                            <Edit3 className="h-4 w-4" />
                            Edit First POM
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const renderDeleteModal = () => {
        if (!pendingDeletePom) return null;

        return ReactDOM.createPortal(
            <div className="modal-overlay" role="presentation" onClick={closeDeleteModal}>
                <div
                    className="modal-shell max-w-md"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-pom-title"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="border-b border-red-200 bg-gradient-to-r from-red-600 to-rose-600 px-5 py-4 text-white">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h4 id="delete-pom-title" className="text-lg font-semibold">Delete POM</h4>
                                <p className="mt-1 text-sm text-red-100">
                                    Remove <span className="font-semibold">{pendingDeletePom}</span>? This action cannot be undone.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                disabled={loading}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                                aria-label="Close delete modal"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 bg-white px-5 py-4">
                        <button
                            type="button"
                            onClick={closeDeleteModal}
                            disabled={loading}
                            className="secondary-btn px-4 py-2"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmDeletePOM}
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            Delete
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const primaryListActionLabel = isFeatureMode
        ? pomsList.length > 0 ? 'Regenerate POMs' : 'Generate POMs'
        : isApplicationCatalog
            ? 'Manage Page Objects'
            : 'Create POM Page';

    const primaryListActionIcon = isApplicationCatalog ? Wrench : Plus;
    const PrimaryListActionIcon = featurePomLoading ? Loader2 : primaryListActionIcon;

    if (viewMode === 'list') {
        return (
            <div className="space-y-4">
                <section className="soft-card border-slate-200 bg-white p-4 md:p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700 shadow-sm">
                                    <List className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">
                                        Automation Assets
                                    </p>
                                    <h3 className="mt-1 text-xl font-bold text-slate-900">Page Object Models</h3>
                                </div>
                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                    Shared across the application
                                </span>
                            </div>
                            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                                {isFeatureMode
                                    ? 'Review page objects generated from the captured pages and locators.'
                                    : 'Create and maintain reusable pages here, then continue to automation without losing context.'}
                            </p>
                        </div>

                        <div className="flex w-full flex-col gap-2 lg:w-auto lg:min-w-[320px] lg:items-end">
                            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                                <button
                                    type="button"
                                    onClick={isApplicationCatalog ? handleManageApplicationPoms : handleCreatePOM}
                                    disabled={featurePomLoading}
                                    className="primary-btn h-9 w-full px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                >
                                    <PrimaryListActionIcon className={`h-4 w-4 ${featurePomLoading ? 'animate-spin' : ''}`} />
                                    {primaryListActionLabel}
                                </button>
                                {isApplicationCatalog && (
                                    <button
                                        type="button"
                                        onClick={fetchPOMsList}
                                        disabled={loadingList}
                                        className="secondary-btn h-9 w-full px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                    >
                                        {loadingList ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4" />
                                        )}
                                        Refresh POMs
                                    </button>
                                )}
                                {showBackButton && onBack && (
                                    <button
                                        type="button"
                                        onClick={onBack}
                                        className="secondary-btn h-9 w-full px-4 py-2 text-sm sm:w-auto"
                                    >
                                        {backLabel}
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">POMs</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalPoms}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Locators</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalLocators}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Methods</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalMethods}</p>
                        </div>
                    </div>

                </section>

                {!isFeatureMode && (
                    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                            <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                                <Wrench className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-slate-900">
                                    Manage page objects from the application
                                </h3>
                                <p className="mt-1 text-sm text-slate-600">
                                    {isApplicationCatalog
                                        ? (
                                            <>
                                                This catalog is shared across every testing area in the application.
                                                Open <strong>Manage Page Objects</strong> to create, scan, edit,
                                                or delete POMs, then return here to continue automation.
                                            </>
                                        )
                                        : (
                                            <>
                                                Page objects are shared across every testing area of an application.
                                                Open your application and choose <strong>Manage POMs</strong> to enter a
                                                page URL, scan it with the POM Scanner extension, and save it. Anything
                                                you create there is selectable here and on the Scripts step.
                                            </>
                                        )}
                                </p>
                            </div>
                        </div>
                    </section>
                )}

                {(listError || error) && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span>{listError || error}</span>
                    </div>
                )}

                {success && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5" />
                        <span>{success}</span>
                    </div>
                )}

                {loadingList ? (
                    <div className="space-y-3">
                        {[0, 1, 2].map((item) => (
                            <div
                                key={item}
                                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:p-4"
                                aria-hidden="true"
                            >
                                <div className="flex flex-col gap-4 md:flex-row md:items-center">
                                    <div className="h-32 w-full shrink-0 animate-pulse rounded-lg bg-slate-100 md:w-48" />
                                    <div className="min-w-0 flex-1 space-y-3">
                                        <div className="h-4 w-48 animate-pulse rounded bg-slate-200" />
                                        <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
                                        <div className="flex flex-wrap gap-2">
                                            <span className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
                                            <span className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
                                            <span className="h-7 w-28 animate-pulse rounded-full bg-slate-100" />
                                        </div>
                                    </div>
                                    <div className="grid shrink-0 grid-cols-2 gap-2 md:w-44">
                                        <span className="h-9 animate-pulse rounded-xl bg-slate-100" />
                                        <span className="h-9 animate-pulse rounded-xl bg-slate-100" />
                                        <span className="col-span-2 h-9 animate-pulse rounded-xl bg-slate-100" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : pomsList.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm">
                        <FileText className="mx-auto h-10 w-10 text-slate-400" />
                        <h4 className="mt-3 text-base font-semibold text-slate-900">
                            {isFeatureMode
                                ? 'No page objects generated yet'
                                : isApplicationCatalog
                                    ? 'No shared page objects yet'
                                    : 'No POMs created yet'}
                        </h4>
                        <p className="mt-2 max-w-2xl mx-auto text-sm leading-6 text-slate-600">
                            {isFeatureMode
                                ? 'Generate page objects from the captured feature flow before moving to scripts.'
                                : isApplicationCatalog
                                    ? 'Create and scan page objects from the application catalog, then return here to continue automation.'
                                    : 'Create a page shell here, then select it in the Metaz Locators extension to add captured locators.'}
                        </p>
                        {!isFeatureMode && (
                            <div className="mx-auto mt-5 max-w-xl rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
                                {isApplicationCatalog
                                    ? 'The application Page Objects area is the source of truth for POM creation, editing and approval.'
                                    : 'Open Metaz Locators, sign in with Google, choose this processed PDF, select the POM page, then save captured locators.'}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={isApplicationCatalog ? handleManageApplicationPoms : handleCreatePOM}
                            disabled={featurePomLoading}
                            className="primary-btn mx-auto mt-4 h-9 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <PrimaryListActionIcon className={`h-4 w-4 ${featurePomLoading ? 'animate-spin' : ''}`} />
                            {primaryListActionLabel}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {pomsList.map((pom, idx) => {
                            const savedPageUrl = getPOMPageUrl(pom);
                            const hasFullPageUrl = isAbsoluteHttpUrl(savedPageUrl);
                            const reviewBadges = scannerReviewBadges(pom);
                            const isShared = isApplicationPom(pom);
                            const scannerScreenshotUrl = isFeatureMode
                                ? ''
                                : isApplicationCatalog
                                    ? buildApplicationScannerAssetUrl(scopedApplicationId, pom.pageName, pom.screenshot)
                                    : buildScannerAssetUrl(pdfId, pom.pageName, pom.screenshot);
                            return (
                            <article
                                key={`${pom.pageName}-${idx}`}
                                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:border-sky-200 hover:shadow-[0_12px_30px_-24px_rgba(15,23,42,0.9)] md:p-4"
                            >
                                <div className="flex flex-col gap-4 md:flex-row md:items-stretch">
                                    <button
                                        type="button"
                                        onClick={() => openPOM(pom, 'view')}
                                        className="flex min-w-0 flex-1 flex-col gap-4 rounded-lg text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 md:flex-row"
                                        aria-label={`View ${pom.pageName}`}
                                    >
                                        <div className="flex h-32 w-full shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 md:w-48">
                                            {scannerScreenshotUrl ? (
                                                <img
                                                    src={scannerScreenshotUrl}
                                                    alt={`${pom.pageName} scanned page`}
                                                    loading="lazy"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none';
                                                    }}
                                                    className="h-full w-full object-cover object-top"
                                                />
                                            ) : (
                                                <FileText className="h-8 w-8 text-slate-400" />
                                            )}
                                        </div>

                                        <div className="flex min-w-0 flex-1 flex-col justify-between gap-3 py-1">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-start justify-between gap-2">
                                                    <h4 className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900">
                                                        {pom.pageName}
                                                    </h4>
                                                    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                                        {isFeatureMode ? 'Generated' : (pom.source === 'metaz_locators_extension' ? 'Extension' : 'Saved')}
                                                    </span>
                                                </div>
                                                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                                                    <Link2 className="h-3.5 w-3.5 text-slate-500" />
                                                    <span className={`truncate ${hasFullPageUrl || isFeatureMode ? '' : 'font-medium text-amber-700'}`}>
                                                        {hasFullPageUrl || isFeatureMode ? savedPageUrl || 'Captured page' : 'Full page URL required'}
                                                    </span>
                                                </p>
                                            </div>

                                            <div className="flex flex-wrap gap-2 text-xs">
                                                <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 font-semibold text-blue-800">
                                                    {pom.locators} locator{Number(pom.locators) === 1 ? '' : 's'}
                                                </span>
                                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800">
                                                    {pom.functions} method{Number(pom.functions) === 1 ? '' : 's'}
                                                </span>
                                                {pom.generatedOn && (
                                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-medium text-slate-600">
                                                        Updated {new Date(pom.generatedOn).toLocaleString()}
                                                    </span>
                                                )}
                                                {reviewBadges.map((badge) => (
                                                    <span
                                                        key={badge.key}
                                                        className={`rounded-full border px-2.5 py-1 font-semibold ${badge.className}`}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </button>

                                    {isFeatureMode ? (
                                        <div className="grid shrink-0 grid-cols-2 gap-2 md:w-44">
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'view')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Eye className="h-4 w-4" />
                                                View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'edit')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                                Edit
                                            </button>
                                            <span className="col-span-2 inline-flex w-full items-center justify-center rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800">
                                                Auto-selected
                                            </span>
                                        </div>
                                    ) : isShared ? (
                                        <div className="grid shrink-0 grid-cols-2 gap-2 md:w-44">
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'view')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Eye className="h-4 w-4" />
                                                View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'edit')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleManageApplicationPoms}
                                                className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
                                            >
                                                <Wrench className="h-4 w-4" />
                                                Manage
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid shrink-0 grid-cols-2 gap-2 md:w-44">
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'view')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Eye className="h-4 w-4" />
                                                View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openPOM(pom, 'edit')}
                                                className="secondary-btn px-3 py-2 text-sm"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => publishPOMGlobally(pom.pageName)}
                                                disabled={publishingPomNames.has(pom.pageName)}
                                                className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {publishingPomNames.has(pom.pageName) ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Link2 className="h-4 w-4" />
                                                )}
                                                Publish Global
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPendingDeletePom(pom.pageName)}
                                                className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </article>
                            );
                        })}
                    </div>
                )}

                        {onNext && pomsList.length > 0 && (
                    <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleContinueToSaveTests}
                                disabled={continueToAutomationLoading || continueDisabled}
                                title={continueDisabled ? continueDisabledReason : undefined}
                                className="primary-btn h-9 px-4 py-2 text-sm bg-gradient-to-r from-slate-900 to-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {continueToAutomationLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        {isFeatureMode ? 'Continue to Scripts' : 'Continue to Automation'}
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </section>
                )}

                {renderFullPageUrlModal()}
                {renderDeleteModal()}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <section className="soft-card border-indigo-200/70 bg-gradient-to-br from-indigo-50 via-blue-50 to-white p-4 md:p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-200 bg-white text-indigo-700 shadow-sm">
                            <FileText className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700">
                                Automation Builder
                            </p>
                            <h3 className="mt-1 text-lg font-bold text-slate-900">{modeTitle}</h3>
                            <p className="mt-1 text-sm text-slate-600">{modeDescription}</p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-sm text-slate-700">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Current Draft</p>
                        <p className="mt-1 font-semibold text-slate-900">{draftSummary}</p>
                        {(selectedPOMName || pomName) && (
                            <p className="mt-1 text-xs text-slate-600">
                                {isReadOnly ? 'Viewing' : 'Working on'} {selectedPOMName || pomName}
                            </p>
                        )}
                        {viewMode === 'edit' && autosaveMessage && (
                            <p
                                className={`mt-1 text-xs font-semibold ${
                                    autosaveStatus === 'error' ? 'text-red-600' : 'text-emerald-700'
                                }`}
                            >
                                {autosaveMessage}
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <span>{success}</span>
                </div>
            )}

            {loading && (
                <div className="soft-card-muted flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                </div>
            )}

            {!isCreateView && selectedPOMName && (
                <section className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                        <button
                            type="button"
                            onClick={() => handleEditorTabChange('structured')}
                            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                                editorTab === 'structured'
                                    ? 'bg-white text-indigo-700 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <List className="h-4 w-4" />
                            Structured
                        </button>
                        <button
                            type="button"
                            onClick={() => handleEditorTabChange('files')}
                            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${
                                editorTab === 'files'
                                    ? 'bg-white text-indigo-700 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <FileCode className="h-4 w-4" />
                            File View
                        </button>
                    </div>
                </section>
            )}

            {!isCreateView && editorTab === 'files' ? renderPOMFileView() : (
            <>
            <section className="soft-card space-y-4">
                <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-blue-700" />
                    <h4 className="text-sm font-semibold text-slate-900">Page Details</h4>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Page Object Model Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={pomName}
                            onChange={(event) => setPomName(event.target.value)}
                            disabled={isReadOnly || (isFeatureMode && viewMode === 'edit')}
                            placeholder="LoginPage"
                            className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                        <p className="mt-1 text-xs text-slate-500">Use PascalCase naming.</p>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Page URL <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="url"
                            value={pomUrl}
                            onChange={(event) => setPomUrl(event.target.value)}
                            disabled={isReadOnly}
                            placeholder="https://your-app.example.com/login"
                            className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                        <p className="mt-1 text-xs text-slate-500">Enter the full website URL opened by this POM.</p>
                    </div>
                </div>
            </section>

            {selectedPomImageUrl && (
                <section className="soft-card space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold text-slate-900">
                            Captured Page
                        </h4>
                    </div>
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                        <img
                            src={selectedPomImageUrl}
                            alt={`${pomName || 'Captured page'} screen`}
                            onError={(event) => {
                                event.currentTarget.parentElement.style.display = 'none';
                            }}
                            className="max-h-[520px] w-full object-contain"
                        />
                    </div>
                </section>
            )}

            <section className="soft-card space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                        Locators <span className="text-slate-500">({locators.length})</span>
                    </h4>
                    {!isReadOnly && (
                        <button type="button" onClick={addLocator} className="secondary-btn px-3 py-2 text-sm">
                            <Plus className="h-4 w-4" />
                            Add Locator
                        </button>
                    )}
                </div>

                {locators.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-600">
                        No locators added yet. Save this page now, then add locators from the Metaz Locators extension.
                    </div>
                )}

                <div className="space-y-2">
                    {locators.map((loc) => {
                        const isExpanded = expandedLocator === loc.id;
                        return (
                            <article key={loc.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setExpandedLocator(isExpanded ? null : loc.id)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">{loc.name || 'Unnamed locator'}</p>
                                        <p className="mt-0.5 truncate text-xs text-slate-500">
                                            {loc.type === 'role'
                                                ? `${loc.type} • ${loc.role || 'No role'}${loc.options ? ` • ${loc.options}` : ''}`
                                                : `${loc.type} • ${loc.value || 'No value yet'}`}
                                        </p>
                                    </div>
                                    {isExpanded ? (
                                        <ChevronUp className="h-4 w-4 text-slate-500" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>

                                {isExpanded && (
                                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/80 p-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={loc.name}
                                                    onChange={(event) => updateLocator(loc.id, 'name', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="emailInput"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>

                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Type <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={loc.type}
                                                    onChange={(event) => updateLocator(loc.id, 'type', event.target.value)}
                                                    disabled={isReadOnly}
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                >
                                                    {locatorTypes.map((type) => (
                                                        <option key={type} value={type}>
                                                            {type}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {loc.type === 'role' ? (
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                        Role <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        value={loc.role}
                                                        onChange={(event) => updateLocator(loc.id, 'role', event.target.value)}
                                                        disabled={isReadOnly}
                                                        className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                    >
                                                        {roleOptions.map((role) => (
                                                            <option key={role} value={role}>
                                                                {role}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                        Options
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={loc.options}
                                                        onChange={(event) => updateLocator(loc.id, 'options', event.target.value)}
                                                        disabled={isReadOnly}
                                                        placeholder="name: 'Login'"
                                                        className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Value <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={loc.value}
                                                    onChange={(event) => updateLocator(loc.id, 'value', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="Email"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>
                                        )}

                                        {loc.type === 'text' && (
                                            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(loc.exact)}
                                                    onChange={(event) => updateLocator(loc.id, 'exact', event.target.checked)}
                                                    disabled={isReadOnly}
                                                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                                                />
                                                Exact match
                                            </label>
                                        )}

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                value={loc.description}
                                                onChange={(event) => updateLocator(loc.id, 'description', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="Email input field"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        {!isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => deleteLocator(loc.id)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete Locator
                                            </button>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="soft-card space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                        Methods <span className="text-slate-500">({functions.length})</span>
                    </h4>
                    {!isReadOnly && (
                        <button type="button" onClick={addFunction} className="secondary-btn px-3 py-2 text-sm">
                            <Plus className="h-4 w-4" />
                            Add Method
                        </button>
                    )}
                </div>

                {functions.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-600">
                        No methods added yet.
                    </div>
                )}

                <div className="space-y-2">
                    {functions.map((func) => {
                        const isExpanded = expandedFunction === func.id;
                        return (
                            <article key={func.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setExpandedFunction(isExpanded ? null : func.id)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">{func.name || 'Unnamed method'}</p>
                                        <p className="mt-0.5 truncate text-xs text-slate-500">{func.description || 'No description yet'}</p>
                                    </div>
                                    {isExpanded ? (
                                        <ChevronUp className="h-4 w-4 text-slate-500" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>

                                {isExpanded && (
                                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/80 p-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={func.name}
                                                    onChange={(event) => updateFunction(func.id, 'name', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="login"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Return Type
                                                </label>
                                                <input
                                                    type="text"
                                                    value={func.returnType}
                                                    onChange={(event) => updateFunction(func.id, 'returnType', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="Promise<void>"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Description <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={func.description}
                                                onChange={(event) => updateFunction(func.id, 'description', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="Login with email and password"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Parameters
                                            </label>
                                            <input
                                                type="text"
                                                value={func.parameters}
                                                onChange={(event) => updateFunction(func.id, 'parameters', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="email: string, password: string"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                            <p className="mt-1 text-xs text-slate-500">Comma-separated parameter signatures.</p>
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Body <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                value={func.body}
                                                onChange={(event) => updateFunction(func.id, 'body', event.target.value)}
                                                disabled={isReadOnly}
                                                rows={5}
                                                placeholder="await this.emailInput.fill(email);"
                                                className="text-input font-mono disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        {!isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => deleteFunction(func.id)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete Method
                                            </button>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>
            </>
            )}

            <section className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        type="button"
                        onClick={async () => {
                            await persistPOMEdit({ keepalive: false, silent: true }).catch(() => null);
                            if (isApplicationCatalog && onBack) {
                                onBack();
                                return;
                            }
                            resetEditorState();
                            setViewMode('list');
                        }}
                        className="secondary-btn"
                    >
                        Back to List
                    </button>

                    <div className="flex flex-wrap gap-2 sm:justify-end">
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={editorTab === 'files' && !isCreateView ? savePOMFiles : handleSavePOM}
                                disabled={loading || savingPomFiles}
                                className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600"
                            >
                                {loading || savingPomFiles ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : editorTab === 'files' && !isCreateView ? (
                                    <Save className="h-4 w-4" />
                                ) : (
                                    <Wrench className="h-4 w-4" />
                                )}
                                {loading || savingPomFiles
                                    ? 'Saving...'
                                    : editorTab === 'files' && !isCreateView ? 'Save Files' : 'Save POM'}
                            </button>
                        )}

                        {onNext && (
                            <button
                                type="button"
                                onClick={handleContinueToSaveTests}
                                disabled={loading || !hasSavedPOM || continueToAutomationLoading || continueDisabled}
                                title={continueDisabled ? continueDisabledReason : undefined}
                                className="primary-btn bg-gradient-to-r from-indigo-600 to-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {continueToAutomationLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        Continue to Automation
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {!isReadOnly && (locators.length > 0 || functions.length > 0) && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    <span className="font-semibold">Draft Summary:</span> {draftSummary}.
                </div>
            )}

            {renderDeleteModal()}
            {renderFullPageUrlModal()}
        </div>
    );
};
